// src/components/Control/ControlPanel/CameraSettings.tsx
// Настройки камер робота. «Передавать» и частота кадров, детекция препятствий
// по камере 2 и «стоп перед препятствием» хранятся на роботе: изменения уходят
// в robot/<id>/config (fleet_adapter сливает их с текущими и сохраняет), текущие
// значения приходят в robot/<id>/status -> "settings". «Показывать» — только в
// этом браузере (localStorage).
import React, { useRef, useState } from 'react';
import * as zenoh from '@eclipse-zenoh/zenoh-ts';
import { useHasCam2, useTelemetryState } from '../robot/RobotContext';

export type CamId = '0' | '1';

interface CamCfg {
  stream: boolean;
  fps: number;
}

interface Cam2Cfg {
  detector: 'esp' | 'rpi' | 'off';
  sensitivity: number;
  roi_top: number;
  near: number;
  min_cols: number;
  period_ms: number;
  framesize: number;
  quality: number;
  hflip: boolean;
  vflip: boolean;
}

// one-shot actions for the floor colour, not saved on the robot
type Cam2Update = Partial<Cam2Cfg> & { calibrate?: boolean; floor?: 'auto' };

interface Settings {
  cams: Record<CamId, CamCfg>;
  cam2: Cam2Cfg;
  stop_on_obstacle: boolean;
}

interface Cam2Summary {
  online: boolean;
  fps?: number;
  firmware?: string;
  rssi?: number;
  esp_settings?: { floor_fixed?: boolean };
}

interface Props {
  zenohSession: zenoh.Session | null;
  robotId: string;
  show: Record<CamId, boolean>;
  onShowChange: (cam: CamId, show: boolean) => void;
}

const CAM_NAMES: Record<CamId, string> = { '0': 'Камера 1 (Pi)', '1': 'Камера 2 (ESP32-S3)' };
// esp32-camera framesize_t (with FRAMESIZE_320X320 in the enum): QVGA 6, VGA 10, SVGA 11
const FRAMESIZES: [number, string][] = [[6, '320×240'], [10, '640×480'], [11, '800×600']];

const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, margin: '4px 0', fontSize: 13, flexWrap: 'wrap' };
const label: React.CSSProperties = { flex: '0 0 150px', color: '#444' };

const CameraSettings: React.FC<Props> = ({ zenohSession, robotId, show, onShowChange }) => {
  // Текущие значения — из общей телеметрии (robot/<id>/status)
  const settings = useTelemetryState(t => (t.status?.settings?.cams ? t.status.settings : null) as unknown as Settings | null);
  const cam2 = useTelemetryState(t => (t.status?.cam2 ?? null) as Cam2Summary | null);
  // у Pi без шасси камеры 2 обычно нет
  const hasCam2 = useHasCam2() && !!settings?.cams['1'] && !!settings.cam2;
  // local copy of slider values while the user drags, so status updates don't fight the slider
  const [draft, setDraft] = useState<Partial<Cam2Cfg>>({});
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const send = (update: object) => {
    zenohSession?.put(`robot/${robotId}/config`, JSON.stringify(update)).catch(err =>
      console.warn('CameraSettings: put failed', err));
  };

  const setCam = (cam: CamId, patch: Partial<CamCfg>) => send({ cams: { [cam]: patch } });
  const setCam2Now = (patch: Cam2Update) => send({ cam2: patch });
  // sliders: show the value at once, send it when the user stops moving it
  const setCam2Debounced = (patch: Partial<Cam2Cfg>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      send({ cam2: next });
      setTimeout(() => setDraft({}), 1500);  // then follow the robot's values again
    }, 400);
  };

  if (!zenohSession) return null;
  if (!settings) {
    return (
      <div className="feature-card camera-settings">
        <h3><span>Камеры</span></h3>
        Нет настроек от робота (стек на Pi не запущен?)
      </div>
    );
  }
  const c2 = { ...settings.cam2, ...draft };
  const slider = (key: keyof Cam2Cfg, name: string, min: number, max: number, step: number, fmt: (v: number) => string) => (
    <div style={row}>
      <span style={label}>{name}</span>
      <input type="range" min={min} max={max} step={step} value={c2[key] as number}
             disabled={c2.detector === 'off'}
             onChange={e => setCam2Debounced({ [key]: Number(e.target.value) } as Partial<Cam2Cfg>)} style={{ flex: 1 }} />
      <span style={{ width: 40, textAlign: 'right' }}>{fmt(c2[key] as number)}</span>
    </div>
  );

  return (
    <div className="feature-card camera-settings">
      <h3><span>Камеры</span></h3>
      {(hasCam2 ? ['0', '1'] as CamId[] : ['0'] as CamId[]).map(cam => (
        <div key={cam} style={row}>
          <span style={label}>{CAM_NAMES[cam]}</span>
          <label title="Робот передаёт видео на сайт (выключите, чтобы не занимать Wi-Fi)">
            <input type="checkbox" checked={settings.cams[cam].stream}
                   onChange={e => setCam(cam, { stream: e.target.checked })} /> передавать
          </label>
          <select value={settings.cams[cam].fps} onChange={e => setCam(cam, { fps: Number(e.target.value) })}
                  disabled={!settings.cams[cam].stream}>
            {[2, 5, 10, 15].map(f => <option key={f} value={f}>{f} к/с</option>)}
          </select>
          <label title="Показывать видео на этой странице (только в этом браузере)">
            <input type="checkbox" checked={show[cam]} onChange={e => onShowChange(cam, e.target.checked)} /> показывать
          </label>
        </div>
      ))}
      {hasCam2 && <>
      <div style={{ fontSize: 12, color: '#666', margin: '2px 0 8px' }}>
        Камера 2: {cam2?.online ? `на связи, ${cam2.fps ?? 0} к/с${cam2.rssi != null ? `, Wi-Fi ${cam2.rssi} дБм` : ''}${cam2.firmware ? `, прошивка ${cam2.firmware}` : ''}` : 'не на связи'}
      </div>

      <h4 style={{ margin: '8px 0 4px' }}>Препятствия по камере 2</h4>
      <div style={row}>
        <span style={label}>Детекция</span>
        <select value={c2.detector} onChange={e => setCam2Now({ detector: e.target.value as Cam2Cfg['detector'] })}>
          <option value="esp">на ESP32 (камера)</option>
          <option value="rpi">на Raspberry Pi</option>
          <option value="off">выключена</option>
        </select>
      </div>
      {slider('sensitivity', 'Порог отличия от пола', 10, 250, 5, v => String(v))}
      {slider('roi_top', 'Верх зоны поиска', 0.1, 0.9, 0.05, v => `${Math.round(v * 100)}%`)}
      {slider('near', 'Дистанция «близко»', 0.1, 1, 0.05, v => `${Math.round(v * 100)}%`)}
      {slider('min_cols', 'Доля столбцов', 0.05, 1, 0.05, v => `${Math.round(v * 100)}%`)}
      <div style={row}>
        <span style={label}>Цвет пола</span>
        <button disabled={c2.detector === 'off'} onClick={() => setCam2Now({ calibrate: true })}
                title="Робот стоит, перед ним чистый пол: запомнить его цвет">Запомнить пол</button>
        <button disabled={c2.detector === 'off'} onClick={() => setCam2Now({ floor: 'auto' })}
                title="Снова брать цвет пола из низа кадра">Авто</button>
      </div>
      <div style={row}>
        <span style={label}>Стоп перед препятствием</span>
        <label title="Команды «Вперёд» с сайта не выполняются, пока по центру препятствие (повороты разрешены)">
          <input type="checkbox" checked={settings.stop_on_obstacle}
                 onChange={e => send({ stop_on_obstacle: e.target.checked })} /> не ехать вперёд
        </label>
      </div>

      <h4 style={{ margin: '8px 0 4px' }}>Изображение камеры 2</h4>
      <div style={row}>
        <span style={label}>Разрешение / качество</span>
        <select value={c2.framesize} onChange={e => setCam2Now({ framesize: Number(e.target.value) })}>
          {FRAMESIZES.map(([v, n]) => <option key={v} value={v}>{n}</option>)}
        </select>
        <select value={c2.quality} onChange={e => setCam2Now({ quality: Number(e.target.value) })}
                title="JPEG: меньше число — лучше качество и больше трафик">
          {[8, 10, 12, 15, 20, 30].map(q => <option key={q} value={q}>q {q}</option>)}
        </select>
      </div>
      <div style={row}>
        <span style={label}>Отражение</span>
        <label><input type="checkbox" checked={c2.hflip} onChange={e => setCam2Now({ hflip: e.target.checked })} /> по горизонтали</label>
        <label><input type="checkbox" checked={c2.vflip} onChange={e => setCam2Now({ vflip: e.target.checked })} /> по вертикали</label>
      </div>
      </>}
    </div>
  );
};

export default CameraSettings;
